import { useHookstate } from "@hookstate/core";
import { RefObject, useCallback, useEffect } from "react";
import { simpleGetter } from "../../../actions/common";
import { userApi } from "../../../api/api";
import Tick from "../../../assets/icons/Tick";
import { useUsersWithSocialScoreAndDemerits } from "../../../hooks/demerit-typed";
import { useDebounce } from "../../../hooks/ui";
import { useUniversalFindAll } from "../../../hooks/universal";
import { accessesTableList } from "../../../misc/constants";
import { User } from "../../../misc/types";
import { getRolesSegmented, getSegmentedRolesOnly, showSnackbar } from "../../../misc/utils";
import { useGlobalMiscState } from "../../../stores/misc";
import { MainUserWithRoles, Role } from "../../../types";
import { Avatar } from "../../Avatar";

type Props = {
  accessesTableUsers: User[];
  deleteUser: (value: string) => void;
  isChangeTable: boolean;
  updateUserAccesses: (email: string, accesses: User["accesses"]) => void;
  usersListRef: RefObject<HTMLDivElement | null>
  visibleWidth?: number | string;
  columnMinWidth?: number | string;
  userColumnWidth?: number | string;
};

type AccessesTableState = {
  search: string;
  page: number;
  loading: boolean;
  total: number;
  usersList: MainUserWithRoles[];
}

export default function AccessesTable({
  accessesTableUsers,
  deleteUser,
  isChangeTable,
  updateUserAccesses,
  usersListRef,
  visibleWidth = '100%',
  columnMinWidth = 'clamp(160px, 10vw, 200px)',
  userColumnWidth = 'clamp(260px, 24vw, 340px)'
}: Props) {
  const localState = useHookstate<AccessesTableState>({
    search: '',
    page: 0,
    loading: false,
    total: 0,
    usersList: [],
  });
  const state = localState.get();
  const miscState = useGlobalMiscState();
  const page = state.page;
  const accessesTableSearchValue = miscState.get('accessesTableSearchValue') || ""
  const debouncedQuery = useDebounce(accessesTableSearchValue, 1000);
  const accessesMainUsersTriggerFetch = miscState.get('accessesMainUsersTriggerFetch') || 0
  // const allUsersWithRolesRaw: MainUserWithRoles[] = useUniversalFind('MainUser', {}, {
  //   include: [
  //     {
  //       model: 'Role',
  //       as: 'roles',
  //       through: {
  //         attributes: []
  //       }
  //     }
  //   ]
  // }, accessesMainUsersTriggerFetch)
  // const allUsersWithRoles = allUsersWithRolesRaw.filter(u => {
  //   const queryLc = accessesTableSearchValue.toLowerCase()
  //   return u.json_saved_data.email?.toLowerCase().includes(queryLc) ||
  //     u.json_saved_data.firstname?.toLowerCase().includes(queryLc) ||
  //     u.json_saved_data.lastname?.toLowerCase().includes(queryLc)
  // })

  const postUpdateStudentsWithRoles = (newList: MainUserWithRoles[]) => {
    localState.usersList.set(newList);
  }

  useEffect(() => {
    localState.page.set(0);
  }, [debouncedQuery])

  const [allUsersWithRoles, allUsersLoading, allUsersTotal] =
    useUsersWithSocialScoreAndDemerits<"roles">(accessesMainUsersTriggerFetch, {
      page,
      type: 'roles',
      usersWhere: {
        query: debouncedQuery
      }
    }, postUpdateStudentsWithRoles)

  useEffect(() => {
    localState.merge({
      loading: allUsersLoading,
      total: allUsersTotal
    })
  }, [allUsersLoading, allUsersTotal])
  const roles = useUniversalFindAll('Role')
  const columnWidths = accessesTableList.map((item) =>
    item.nested.length > 1 ? 17 : 9
  );
  const rolesSegmented = getRolesSegmented(roles)
  const rolesOnly: Role[] = getSegmentedRolesOnly(rolesSegmented)

  const handleScroll = useCallback(() => {
    if (usersListRef.current) {
      const stateLocal = localState.get()
      const usersList = stateLocal.usersList || []
      const total = stateLocal.total || 0
      const loading = stateLocal.loading || false
      const { scrollTop, scrollHeight, clientHeight } = usersListRef.current;
      console.log(scrollHeight, scrollTop, clientHeight, scrollHeight - scrollTop <= clientHeight * 1, usersList.length, total, usersList.length < total, loading)
      if (scrollHeight - scrollTop <= clientHeight * 1 && usersList.length < total && !loading) {
        localState.merge({
          page: (stateLocal.page || 0) + 1
        })
      }
    }
  }, [])

  useEffect(() => {
    if (usersListRef.current) {
      usersListRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (usersListRef.current) {
        usersListRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [])

  const onPressRole = (user: MainUserWithRoles, role: Role) => async () => {
    const hasRole = Boolean(user.roles.find(ur => ur.id === role.id))
    const response = await simpleGetter(userApi.updateRole, {
      main_user_id: user.id,
      role_id: role.id,
      action: hasRole ? 'remove' : 'add'
    })
    showSnackbar(response.success ? 'Успешно' : 'Произошла ошибка', response.success ? 'success' : 'error')
    if (response.success) {
      miscState.set('accessesMainUsersTriggerFetch', accessesMainUsersTriggerFetch + 1)
    }
  }

  const wrapperStyle: React.CSSProperties = {
    width: typeof visibleWidth === 'number' ? `${visibleWidth}px` : visibleWidth,
    // @ts-ignore:  
    '--col-min': typeof columnMinWidth === 'number' ? `${columnMinWidth}px` : columnMinWidth,
    // @ts-ignore: 
    '--user-col': typeof userColumnWidth === 'number' ? `${userColumnWidth}px` : userColumnWidth,
  };

  return (
    <div className="relative overflow-x-auto" style={wrapperStyle}>
  <table className="table-auto w-full">
    <thead className="sticky top-0 z-30 bg-white">
          <tr className="border border-[#AFB7CA4D]">
            <th
              rowSpan={2}
      className="py-[19px] px-2 sticky left-0 top-0 z-40 bg-white border-r border-[#AFB7CA4D]"
              style={{ minWidth: 'var(--user-col)' as any, width: 'var(--user-col)' as any }}
            >
              <span className="font-medium text-sm leading-[15.4px] text-valley-gray whitespace-nowrap">
                Пользователь
              </span>
            </th>
            {rolesSegmented.map((sr: any, i: number) => (
              <th
                key={`role-segment-${sr.category_name}-${i}`}
        className={`px-2 border-x border-[#AFB7CA4D] ${i % 2 === 0 ? 'bg-valley-background-blue' : 'bg-[#F0F3FA80]'} text-center bg-white`}
                style={{ minWidth: `calc(${sr.list.length} * var(--col-min))` } as any}
                colSpan={sr.list.length}
              >
                <span
                  className="font-medium text-[13px] leading-snug text-valley-gray whitespace-normal break-words"
                  title={sr.category_name}
                >
                  {sr.category_name}
                </span>
              </th>
            ))}
          </tr>
      <tr className="border border-[#AFB7CA4D] bg-white">
            {rolesOnly.map((r, idx) => {
              const foundIndex = rolesSegmented.findIndex(sr => sr.category_name === r.category_name)
              return (
                <th
                  key={`role-col-${r.id}-${idx}`}
      className={`px-2 border-x border-[#AFB7CA4D] ${foundIndex % 2 === 0 ? 'bg-[#F0F3FA80]' : ''} text-center bg-white`}
                  style={{ minWidth: 'var(--col-min)' as any }}
                >
                  <span
                    className="font-medium text-[13px] leading-snug text-valley-gray whitespace-normal break-words"
                    title={r.label}
                  >
                    {r.label}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {allUsersWithRoles.map((u, uIdx) => (
            <tr key={`user-row-${u.id}-${uIdx}`} className="border border-[#AFB7CA4D]">
              <td
                className="py-[19px] px-2 sticky left-0 z-10 bg-white border-r border-[#AFB7CA4D]"
                style={{ minWidth: 'var(--user-col)' as any, width: 'var(--user-col)' as any }}
              >
                <div className="flex flex-row items-center">
                  <Avatar user={u} />
                  <div className="ml-3 flex flex-col whitespace-nowrap">
                    <span className="font-medium text-[15px] leading-[19.5px] text-valley-black">
                      {u.json_saved_data.lastname} {u.json_saved_data.firstname}
                    </span>
                    <span className="mt-0.5 text-sm leading-[15.4px] text-valley-gray">
                      {u.json_saved_data.email}
                    </span>
                  </div>
                </div>
              </td>
              {rolesOnly.map((r, rIdx) => {
                const foundIndex = rolesSegmented.findIndex(sr => sr.category_name === r.category_name)
                const hasRole = Boolean(u.roles.find(ur => ur.id === r.id))
                return (
                  <td
                    key={`user-${u.id}-role-${r.id}-${rIdx}`}
                    className={`px-2 border-x border-[#AFB7CA4D] ${foundIndex % 2 === 0 ? 'bg-[#F0F3FA80]' : ''} text-center`}
                    style={{ minWidth: 'var(--col-min)' as any }}
                  >
                    <div className="flex flex-row justify-center items-center">
                      <div
                        className={`${hasRole
                          ? 'bg-[#3BBC30] border-[#3BBC30]'
                          : 'bg-white border-[#AFB7CA] hover:border-[#0068FF]'}
                          rounded-md w-6 h-6 cursor-pointer border-2 transition-colors duration-150 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#0068FF]`}
                        onClick={onPressRole(u, r)}
                        role="checkbox"
                        aria-checked={hasRole}
                        tabIndex={0}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPressRole(u, r)(); } }}
                        title={hasRole ? 'Отключить доступ' : 'Включить доступ'}
                      >
                        {hasRole && (
                          <Tick additionalStyle={"w-4 h-4 text-white"} />
                        )}
                      </div>
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )

  // return (
  //   <div className="pl-2 pr-[22px]">
  //     <div className="flex">
  //       <div className="w-[19%] border-t border-[#AFB7CA4D] flex items-center text-sm text-[#5F6B88] font-medium">
  //         Пользователь
  //       </div>
  //       {accessesTableList.map((item, id) => (
  //         <div
  //           key={`access-item-${id}`}
  //           className={`text-sm text-[#5F6B88] font-medium text-center ${
  //             columnWidths[id] === 17 ? "w-[17%]" : "w-[9%]"
  //           }`}
  //         >
  //           <div
  //             className={`border-l border-[#AFB7CA4D] px-2 bg-[#F0F3FA] py-1 ${
  //               id % 2 === 0 ? "" : "bg-opacity-50"
  //             }`}
  //           >
  //             {item.title}
  //           </div>
  //           <div className="flex w-full">
  //             {item.nested.map((nestedItem, nestedId) => (
  //               <div
  //                 key={`nested-item-${id}-${nestedId}`}
  //                 className={`w-full px-2 py-1 border-l border-[#AFB7CA4D] border-opacity-30 ${
  //                   id % 2 === 0 ? "bg-[#F0F3FA80]" : "bg-white"
  //                 }`}
  //               >
  //                 {nestedItem}
  //               </div>
  //             ))}
  //           </div>
  //         </div>
  //       ))}
  //     </div>
  //     {!isChangeTable
  //       ? accessesTableUsers.map((user, userId) => (
  //           <AccessesTableRow
  //             key={`user-row-${userId}`}
  //             user={user}
  //             columnWidths={columnWidths}
  //             deleteUser={deleteUser}
  //           />
  //         ))
  //       : accessesTableUsers.map((user, userId) => (
  //           <AccessesTableRowEdit
  //             key={`user-row-${userId}`}
  //             user={user}
  //             columnWidths={columnWidths}
  //             deleteUser={deleteUser}
  //             updateUserAccesses={updateUserAccesses}
  //           />
  //         ))}
  //   </div>
  // );
}
